import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Flex, Spinner, useBreakpointValue, VStack } from '@chakra-ui/react';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { Alert } from '@components/ui/alert';
import { DialogContent, DialogRoot } from '@components/ui/dialog';
import { WeekShapeValue } from '@blocks/onboarding/v2/WeekShapeStep';
import { AvailabilityValue } from '@blocks/onboarding/v2/AvailabilityStep';
import { useCheckSettings, useSaveTrainingSettings, useSettingsForm, useTrainingSettings } from '@utils/hooks/queries';
import { AthleteSettings } from '@services/engineService';
import { effectiveSessionsPerWeek, feasibilityMessages } from '@utils/v2/feasibilityMessages';
import { settingsChanged } from '@utils/v2/settingsDirty';
import MigrationHero from './MigrationHero';
import MigrationDaysBody from './MigrationDaysBody';

interface Props {
  onConfirmed: () => void;
}

/**
 * PRD 12 §7 — the confirmation raised after the cutover: « Ta semaine type » and « Tes disponibilités »
 * pre-filled with the settings the migration derived, for the athlete to accept or correct.
 *
 * **Mandatory**: no click-away, no escape and no « Plus tard » — the only way out is « Valider mon plan ».
 * Until it is confirmed it returns at every app opening.
 *
 * Confirming goes through the ordinary settings save: unchanged ⇒ the confirmation is recorded and
 * nothing is regenerated or pushed; changed ⇒ a regeneration from tomorrow and a re-push. Both branches
 * already exist in `saveSettings`.
 */
export const MigrationDialog: React.FC<Props> = ({ onConfirmed }) => {
  const { settings: stored, isLoading } = useTrainingSettings();
  const { settingsForm } = useSettingsForm(stored?.planId ?? undefined);
  const save = useSaveTrainingSettings();
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false;

  const [weekShape, setWeekShape] = useState<WeekShapeValue | null>(null);
  const [availability, setAvailability] = useState<AvailabilityValue | null>(null);

  useEffect(() => {
    if (!stored) return;
    setWeekShape({
      sessionsPerWeek: stored.sessionsPerWeek,
      includePpg: stored.includePpg,
      includeTests: stored.includeTests !== false,
    });
    setAvailability({ sportDays: stored.sportDays, brickDow: stored.brickDow });
  }, [stored]);

  const current: AthleteSettings | null = useMemo(
    () =>
      weekShape && availability
        ? {
            sessionsPerWeek: weekShape.sessionsPerWeek,
            sportDays: availability.sportDays,
            brickDow: availability.brickDow,
            includePpg: weekShape.includePpg,
            includeTests: weekShape.includeTests,
          }
        : null,
    [weekShape, availability]
  );

  const { error: feasibilityError } = useCheckSettings(
    stored?.planId ?? undefined,
    current ?? { sessionsPerWeek: {}, sportDays: [], includePpg: false },
    Boolean(current)
  );

  const messages = settingsForm && weekShape
    ? feasibilityMessages(
        feasibilityError,
        effectiveSessionsPerWeek(weekShape.sessionsPerWeek, weekShape.includePpg, settingsForm.ppg.sportId)
      )
    : null;
  const ready = Boolean(!isLoading && stored?.planId && settingsForm && weekShape && availability && current && messages);
  const changed = stored && current ? settingsChanged(current, stored) : false;

  const serverError = save.error
    ? ((save.error as { response?: { data?: { errors?: Array<{ message: string }>; message?: string } } }).response?.data
        ?.errors?.[0]?.message ??
      (save.error as { response?: { data?: { message?: string } } }).response?.data?.message ??
      'Tes réglages n’ont pas pu être enregistrés.')
    : null;

  return (
    <DialogRoot
      open
      placement={isMobile ? 'bottom' : 'center'}
      size={isMobile ? 'full' : 'lg'}
      motionPreset={isMobile ? 'slide-in-bottom' : 'scale'}
      closeOnInteractOutside={false}
      closeOnEscape={false}
    >
      <DialogContent
        display="flex"
        flexDirection="column"
        overflow="hidden"
        bg="white"
        {...(isMobile
          ? { mt: 12, h: 'calc(100dvh - 3rem)', maxH: 'calc(100dvh - 3rem)', roundedTop: '2xl', roundedBottom: 0 }
          : { maxW: '640px', maxH: '90dvh', rounded: '2xl', mx: 4 })}
      >
        <MigrationHero />

        <Box flex={1} minH={0} overflowY="auto" px={{ base: 4, md: 9 }} py={6}>
          {!ready || !settingsForm || !weekShape || !availability || !messages ? (
            <Flex justifyContent="center" py={10}>
              <Spinner />
            </Flex>
          ) : (
            <VStack align="stretch" gap={5}>
              <MigrationDaysBody
                form={settingsForm}
                weekShape={weekShape}
                onWeekShapeChange={setWeekShape}
                availability={availability}
                onAvailabilityChange={setAvailability}
                messages={messages}
              />

              {changed && (
                <Alert status="info" title="Ce que ça change">
                  Tes séances à partir de demain seront replacées. Ton objectif, tes cycles et ta date de compétition ne changent pas.
                  {stored?.hasFutureMoves ? ' Les déplacements que tu as faits sur ces jours seront perdus.' : ''}
                </Alert>
              )}

              {serverError && <Alert status="error" title={serverError} />}
            </VStack>
          )}
        </Box>

        <Flex
          flexShrink={0}
          justifyContent="flex-end"
          alignItems="center"
          gap={3}
          borderTopWidth={1}
          borderColor="gray.200"
          px={{ base: 4, md: 9 }}
          py={4}
          pb={{ base: 'max(1rem, env(safe-area-inset-bottom))', md: 4 }}
        >
          <Button
            colorPalette="blue"
            size="lg"
            rounded="lg"
            fontWeight="bold"
            loading={save.isPending}
            disabled={!ready || feasibilityError !== null}
            onClick={() => current && save.mutate(current, { onSuccess: onConfirmed })}
          >
            Valider mon plan
            <ArrowRightIcon width={18} />
          </Button>
        </Flex>
      </DialogContent>
    </DialogRoot>
  );
};

export default MigrationDialog;
